import type { DocNode } from '@atlaskit/adf-schema';
import { fg } from '@atlaskit/platform-feature-flags';
import type { ConfluenceCreatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands';

import type { DocumentCreateCommandProcessor } from '../stream-processors/create-command-processor';

import { type PassThroughCommandProps } from './command-handler';
import { StreamingCommandHandler } from './streaming-command-handler';

export class CreateCommandHandler extends StreamingCommandHandler<ConfluenceCreatePageCommandProperties> {
	constructor(
		props: PassThroughCommandProps,
		private createCommandProcessor: DocumentCreateCommandProcessor,
	) {
		super(props, 'create');
	}

	process(payload: ConfluenceCreatePageCommandProperties): void {
		if (!payload.data.adf) {
			return;
		}

		if (!this.createCommandProcessor.isInitialized() && payload.data.previousAccumulatedData) {
			// The scenario where the command processor is not initialized but the payload contains
			// accumulated ADF can happen if the user closes and reopens the modal during streaming.
			// To handle this, we first clear the document (in case it first streamed for long enough
			// to trigger a draft sync) and then stream in the accumulated ADF so that streaming
			// subsequent chunks can continue correctly.
			this.props.editorAPI.core.actions.replaceDocument('', {
				addToHistory: false,
				scrollIntoView: false,
			});
			this.createCommandProcessor.stream(payload.data.previousAccumulatedData, false);
		}

		if (!payload.isStreamComplete) {
			this.createCommandProcessor.stream(payload.data.adf, this.isStreamFinished);
		}
	}

	async finishAction(): Promise<DocNode | undefined> {
		const adfDoc = await this.createCommandProcessor.finish({
			onError: this.props.onStreamError,
			onRepair: this.props.onStreamRepair,
		});

		if (adfDoc) {
			this.props.editorAPI.core.actions.replaceDocument(adfDoc, {
				addToHistory: false,
				scrollIntoView: false,
			});
			if (fg('platform_editor_ai_aifc_patch_ga')) {
				// Ensure all expands are open
				this.props.editorAPI.core.actions.execute(
					this.props.editorAPI.expand?.commands.toggleExpandRange(),
				);
			}
		}

		return adfDoc;
	}

	abort(): void {
		this.createCommandProcessor.abort();
	}
}
